import { compose } from 'redux'
import { connect } from 'react-redux'
import { firebaseConnect } from 'react-redux-firebase'
import moment from 'moment'
import { getOffset, participantEvidence } from './ViewUtils'

const TIMEZONEOFFSET = getOffset('Europe/Helsinki')

// Same rule as ActivitiesScreen's notHappen (ActivitiesScreen.js:121-127): an
// activity whose signup window closed with nobody in it never happened, so it
// must not go on a public board - not even for a past week.
// Unlike that version this uses participantEvidence, because participants[] and
// participantscount disagree on most production docs that carry both.
const notHappen = (activity, now) => {
  if (!activity.entryend) return false
  if (!moment.utc(activity.entryend, 'YYYY-MM-DDTHH:mm', true).isBefore(now)) return false
  return participantEvidence(activity) === 0
}

/**
 * Published activities for the tenant as a flat array, for the public embed.
 *
 * Deliberately different from ActivitiesScreen's mapStateToProps:
 *
 * - No `end.isAfter(gtime)` filter. That is what makes ?from= able to look at a
 *   past week. (It is also subtly wrong there: `end` is a Helsinki wall-clock
 *   string parsed with moment.utc() but compared against a real-UTC gtime, so
 *   in summer it keeps activities alive three hours too long.)
 * - No `pinned`-first sort. Chronology wins on a schedule board.
 * - No types/sports derivation. The board has no filter chips, and that loop
 *   calls .toLowerCase() on `type` unguarded.
 * - Filters on `state === 'published'` only. `ispublished` also exists on real
 *   documents but is `false` on all of them, so filtering by it yields nothing.
 *
 * `activities: null` means the subscription has not delivered yet, which the
 * view shows as a spinner; `[]` means there genuinely is nothing.
 */
const mapStateToProps = (state, props) => {
  const gtime = state.globaltime && state.globaltime.gtime
  // gtime starts as [] (reducer.js:294) and is only filled by App.js's favicon
  // HEAD clock fetch. Note an empty array is TRUTHY, so HomeScreenCalendar's
  // `if (!gtime)` guard does not catch it - check for a real moment instead.
  const now = moment.isMoment(gtime)
    ? gtime
    : moment.utc().add(Math.abs(TIMEZONEOFFSET), 'minutes')

  const raw =
    state.firebase.data.activities &&
    state.firebase.data.activities[props.appconfig.accountkey]

  if (!raw) return { activities: null, now }

  const activities = Object.keys(raw)
    .map((id) => ({ id, ...raw[id] }))
    .filter((x) => x && x.state === 'published' && typeof x.start === 'string')
    .filter((x) => !notHappen(x, now))
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0))

  return { activities, now }
}

// Only the activities path - ActivitiesScreen's HOC also subscribes to
// infobanners/textbanners, which would re-render the board on banner traffic.
const withPublicActivities = compose(
  firebaseConnect((props) => [
    {
      path: 'activities/' + props.appconfig.accountkey,
      queryParams: ['orderByChild=displayName'],
    },
  ]),
  connect(mapStateToProps)
)

export default withPublicActivities
